import { SyntheticEvent, useState, useEffect } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Tooltip from '@mui/material/Tooltip'
import SearchIcon from '@mui/icons-material/Search'
import { getAllDiveSites } from '../../services/diveSiteService'
import DiveSiteBrowser from '../DiveSiteBrowser'
import Database from '../../types/Database'

export type DiveSiteSelection = {
  siteName: string
  location: { lat: number; lng: number; displayName: string } | null
  description: string
  depthTier: '18m' | '30m' | '30m+' | null
} | null

export type DiveSiteAutocompleteProps = {
  value: string
  onChange: (value: string) => void
  onSiteSelected: (selection: DiveSiteSelection) => void
  error?: boolean
  helperText?: string
}

const DiveSiteAutocomplete = ({
  value,
  onChange,
  onSiteSelected,
  error,
  helperText
}: DiveSiteAutocompleteProps) => {
  const [sites, setSites] = useState<Database.DiveSite[]>([])
  const [loading, setLoading] = useState(true)
  const [browserOpen, setBrowserOpen] = useState(false)

  useEffect(() => {
    let mounted = true
    getAllDiveSites()
      .then(data => {
        if (mounted) setSites(data)
      })
      .finally(() => {
        if (mounted) setLoading(false)
      })
    return () => {
      mounted = false
    }
  }, [])

  const handleBrowserSelect = (site: Database.DiveSite) => {
    onChange(site.name)
    onSiteSelected({
      siteName: site.name,
      location: {
        lat: site.location.latitude,
        lng: site.location.longitude,
        displayName: site.address
      },
      description: site.description,
      depthTier: site.depthTier ?? null
    })
  }

  const handleInputChange = (
    _event: SyntheticEvent,
    newValue: string,
    reason: string
  ) => {
    if (reason === 'clear') {
      onChange('')
      onSiteSelected(null)
      return
    }
    if (reason === 'input') {
      onSiteSelected(null)
    }
    onChange(newValue)
  }

  const handleChange = (
    _event: SyntheticEvent,
    newValue: string | Database.DiveSite | null
  ) => {
    if (newValue === null || newValue === '') {
      onChange('')
      onSiteSelected(null)
      return
    }

    if (typeof newValue === 'string') {
      // User typed a custom value (freeSolo), no auto-population
      onChange(newValue)
      return
    }

    // User selected a known dive site — auto-populate fields
    onChange(newValue.name)
    onSiteSelected({
      siteName: newValue.name,
      location: {
        lat: newValue.location.latitude,
        lng: newValue.location.longitude,
        displayName: newValue.address
      },
      description: newValue.description,
      depthTier: newValue.depthTier ?? null
    })
  }

  return (
    <>
      <Autocomplete
        freeSolo
        options={sites}
        getOptionLabel={option =>
          typeof option === 'string' ? option : option.name
        }
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            <span>
              {option.name}
              {option.address && (
                <span
                  style={{ color: '#666', marginLeft: 8, fontSize: '0.85em' }}
                >
                  — {option.address}
                </span>
              )}
            </span>
          </li>
        )}
        inputValue={value}
        onInputChange={handleInputChange}
        onChange={handleChange}
        loading={loading}
        filterOptions={(options, state) => {
          const input = state.inputValue.toLowerCase()
          if (!input) return options
          return options.filter(
            o =>
              o.name.toLowerCase().includes(input) ||
              o.address.toLowerCase().includes(input)
          )
        }}
        renderInput={params => (
          <TextField
            {...params}
            label='Site Name'
            fullWidth
            error={error}
            helperText={helperText}
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? (
                      <CircularProgress color='inherit' size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                    <InputAdornment position='end'>
                      <Tooltip title='Browse dive sites nearby'>
                        <IconButton
                          onClick={() => setBrowserOpen(true)}
                          edge='end'
                          aria-label='Browse dive sites'
                          size='small'
                        >
                          <SearchIcon />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  </>
                )
              }
            }}
          />
        )}
      />
      <DiveSiteBrowser
        open={browserOpen}
        onClose={() => setBrowserOpen(false)}
        onSelect={handleBrowserSelect}
      />
    </>
  )
}

export default DiveSiteAutocomplete
